// QuantumPurchaseModal.js - Updated with Invoice Download
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Snackbar
} from '@mui/material';
import {
  CreditCard,
  Payment,
  Close,
  Lock,
  CheckCircle,
  ArrowBack,
  CurrencyExchange,
  Security,
  Download
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const QuantumPurchaseModal = ({ 
  open, 
  onClose, 
  onSuccess, 
  selectedPackage: initialPackage,
  currency = 'INR',
  currencySymbol = '₹',
  convertedPrice = null
}) => {
  const { user, token, getToken, isAuthenticated, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [displayCurrency, setDisplayCurrency] = useState(currency);
  const [displayCurrencySymbol, setDisplayCurrencySymbol] = useState(currencySymbol);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Enhanced token retrieval
  const getAuthToken = () => {
    if (getToken) {
      const authToken = getToken();
      if (authToken) return authToken;
    }
    
    if (token) return token;
    
    const storedToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (storedToken) return storedToken;
    
    const sessionToken = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (sessionToken) return sessionToken;

    console.error('No authentication token found');
    return null;
  };

  // Update package when props change
  useEffect(() => {
    if (initialPackage) {
      setCurrentPackage({
        ...initialPackage,
        displayPrice: convertedPrice !== null ? convertedPrice : initialPackage.inrPrice
      });
      resetModalState();
    }
  }, [initialPackage, convertedPrice]);

  // Update currency display
  useEffect(() => {
    setDisplayCurrency(currency);
    setDisplayCurrencySymbol(currencySymbol);
    
    if (currency && currency !== 'INR' && currentPackage) {
      const estimatedRate = currentPackage.inrPrice / currentPackage.displayPrice;
      setExchangeRate(estimatedRate);
    } else {
      setExchangeRate(1);
    }
  }, [currency, currencySymbol, currentPackage]);

  // Load Razorpay SDK
  useEffect(() => {
    if (open) {
      if (!window.Razorpay) {
        loadRazorpayScript();
      } else {
        setRazorpayLoaded(true);
      }
      resetModalState();
    }
  }, [open]);

  // Add this useEffect to check backend status
  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        await checkPaymentEndpoints();
      } catch (error) {
        console.warn('Backend payment endpoints not available');
      }
    };

    if (open) {
      checkBackendStatus();
    }
  }, [open]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Clean up any stored payment IDs when component unmounts
      if (localStorage.getItem('currentPaymentId')) {
        localStorage.removeItem('currentPaymentId');
      }
    };
  }, []);

  const loadRazorpayScript = () => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      setRazorpayLoaded(true);
      console.log('Razorpay SDK loaded successfully');
    };
    script.onerror = () => {
      setError('Failed to load payment processor. Please refresh the page.');
      setRazorpayLoaded(false);
      setPaymentStatus('error');
    };
    document.head.appendChild(script);
  };

  const resetModalState = () => {
    setPaymentSuccess(false);
    setActiveStep(0);
    setPaymentStatus('idle');
    setError('');
    setLoading(false);
    setInvoiceData(null);
  };

  // Sync step with status
  useEffect(() => {
    const stepMap = {
      'idle': 0,
      'processing': 1,
      'verifying': 2,
      'success': 3,
      'error': 0
    };
    setActiveStep(stepMap[paymentStatus] || 0);
  }, [paymentStatus]);

  // Create Razorpay order
  const createRazorpayOrder = async (pkg, authToken) => {
    try {
      setPaymentStatus('processing');
      
      console.log('Sending package data:', pkg);

      const endpoints = [
        `${process.env.REACT_APP_API_BASE_URL}/api/payments/create-order`,
        `${process.env.REACT_APP_API_BASE_URL}/api/payment/create-order`
      ];

      let order = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const requestData = {
            amount: pkg.inrPrice || pkg.displayPrice || pkg.price,
            currency: 'INR',
            packageData: {
              id: pkg.id,
              name: pkg.name || pkg.id,
              credits: pkg.credits,
              bonus: pkg.bonus || 0,
              inrPrice: pkg.inrPrice || pkg.displayPrice || pkg.price,
              displayPrice: pkg.displayPrice,
              currency: pkg.currency,
              popular: pkg.popular || false
            },
            notes: {
              displayCurrency: displayCurrency,
              displayAmount: pkg.displayPrice,
              credits: pkg.credits + (pkg.bonus || 0),
              packageName: pkg.name || pkg.id
            }
          };

          console.log('Sending to', endpoint, ':', requestData);

          const response = await axios.post(
            endpoint,
            requestData,
            {
              headers: { 
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 15000
            }
          );

          console.log('Response from server:', response.data);

          if (response.data.success && response.data.order) {
            order = response.data.order;
            if (response.data.paymentId) {
              localStorage.setItem('currentPaymentId', response.data.paymentId);
            }
            break;
          }
        } catch (err) {
          lastError = err;
          console.warn(`Endpoint ${endpoint} failed:`, err.response?.data || err.message);
          continue;
        }
      }

      if (!order) {
        const errorMsg = lastError?.response?.data?.error || 
                        lastError?.message || 
                        'No payment endpoint available';
        throw new Error(errorMsg);
      }

      return order;
    } catch (err) {
      console.error('Order creation error:', err);
      setPaymentStatus('error');
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        throw new Error('SESSION_EXPIRED');
      }
      
      throw new Error(err.response?.data?.error || 'Failed to create payment order');
    }
  };

  // Verify Razorpay payment
  const verifyRazorpayPayment = async (paymentData, pkg, authToken) => {
    try {
      setPaymentStatus('verifying');
      
      // Get stored payment ID
      const paymentId = localStorage.getItem('currentPaymentId');
      
      // Try multiple verification endpoints
      const endpoints = [
        `${process.env.REACT_APP_API_BASE_URL}/api/payments/verify-payment`,
        `${process.env.REACT_APP_API_BASE_URL}/api/payment/verify`,
        `${process.env.REACT_APP_API_BASE_URL}/api/verify`
      ];

      let verificationResult = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const response = await axios.post(
            endpoint,
            {
              razorpay_order_id: paymentData.razorpay_order_id,
              razorpay_payment_id: paymentData.razorpay_payment_id,
              razorpay_signature: paymentData.razorpay_signature,
              paymentId: paymentId
            },
            {
              headers: { 
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 15000
            }
          );

          if (response.data.success) {
            verificationResult = response.data;
            // Clear stored payment ID
            localStorage.removeItem('currentPaymentId');
            break;
          }
        } catch (err) {
          lastError = err;
          console.warn(`Endpoint ${endpoint} failed:`, err.message);
          continue;
        }
      }

      if (!verificationResult) {
        throw new Error(lastError?.response?.data?.error || 'Payment verification failed');
      }

      return verificationResult;
    } catch (err) {
      console.error('Payment verification error:', err);
      setPaymentStatus('error');
      
      // Clear stored payment ID on error
      localStorage.removeItem('currentPaymentId');
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        throw new Error('SESSION_EXPIRED');
      }
      
      throw new Error(err.response?.data?.error || 'Payment verification failed');
    }
  };

  // Enhanced payment success handler with invoice download
  const handlePaymentSuccess = async (result) => {
    setPaymentSuccess(true);
    setPaymentStatus('success');

  // Download invoice automatically
  if (result.invoice && result.invoice.downloadUrl) {
    const link = document.createElement('a');
    link.href = result.invoice.downloadUrl;
    link.download = `invoice-${result.invoice.invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
    

    // Store invoice data for download
    if (result.data?.invoice) {
      setInvoiceData(result.data.invoice);
    }
    
    // Update user context
    if (updateUser) {
      await updateUser();
    }
    
    // Show success snackbar
    setShowSuccessSnackbar(true);
    
    if (onSuccess) {
      onSuccess({
        creditsAdded: result.data?.creditsAdded || (currentPackage.credits + (currentPackage.bonus || 0)),
        amount: currentPackage.displayPrice,
        currency: displayCurrency,
        packageId: currentPackage.id,
        newBalance: result.data?.newBalance,
        paymentId: result.data?.paymentId,
        razorpayPaymentId: result.data?.razorpayPaymentId,
        invoice: result.data?.invoice
      });
    }

    // Auto-close after delay
    setTimeout(() => {
      onClose();
    }, 5000);

  // Redirect to success page with data
  const queryParams = new URLSearchParams({
    credits_added: result.creditsAdded,
    package_name: currentPackage?.name || currentPackage?.id,
    payment_id: result.paymentId,
    order_id: result.razorpayOrderId
  }).toString();
  navigate(`/payment/success?${queryParams}`);
  //};


  };

  // Download invoice function
  const downloadInvoice = async () => {
    if (!invoiceData?.downloadUrl) {
      console.error('No invoice download URL available');
      return;
    }

    try {
      const authToken = getAuthToken();
      const response = await axios.get(invoiceData.downloadUrl, {
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceData.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Invoice downloaded successfully');
    } catch (error) {
      console.error('Invoice download failed:', error);
      setError('Failed to download invoice. Please try again later.');
    }
  };

  // Auto-download invoice on success (optional)
  useEffect(() => {
    if (paymentSuccess && invoiceData?.downloadUrl) {
      // Auto-download after a short delay
      const timer = setTimeout(() => {
        downloadInvoice();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [paymentSuccess, invoiceData]);


// ✅ ADD: Better health check function
const checkPaymentHealth = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/payments/health`, {
      timeout: 3000
    });
    
    return response.data.success === true;
  } catch (err) {
    console.warn('Payment health check failed:', err.message);
    return false;
  }
};


  const handlePayment = async () => {
    // Authentication checks
    if (!isAuthenticated || !user) {
      handleAuthenticationError('You must be logged in to purchase credits');
      return;
    }

    if (!currentPackage) {
      setError('No package selected for purchase');
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      handleAuthenticationError('Authentication session expired. Please sign in again.');
      return;
    }

    if (!window.Razorpay && !razorpayLoaded) {
      setError('Payment system is still loading. Please try again in a moment.');
      return;
    }

  // ✅ IMPROVED: Check if payment system is healthy
  const isPaymentHealthy = await checkPaymentHealth();
  if (!isPaymentHealthy) {
    setError('Payment system is currently unavailable. Please try again later.');
    return;
  }

  // ✅ IMPROVED: Check endpoints with better logic
  const endpointsAvailable = await checkPaymentEndpoints();
  if (!endpointsAvailable) {
    setError('Payment endpoints are not responding. Please try again later.');
    return;
  }
    
    setLoading(true);
    setError('');
    setPaymentStatus('processing');

    try {
      const order = await createRazorpayOrder(currentPackage, authToken);
      await initiateRazorpayPayment(order, authToken);
    } catch (err) {
      handlePaymentError(err);
    }
  };

  const handleAuthenticationError = (message) => {
    setError(message);
    setTimeout(() => {
      onClose();
      if (logout) logout();
      navigate('/login');
    }, 2000);
  };

  const initiateRazorpayPayment = async (order, authToken) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: "Quantum ROI Analyzer",
      description: `Purchase of ${currentPackage.credits + (currentPackage.bonus || 0)} energy credits`,
      order_id: order.id,
      handler: async (response) => {
        try {
          const result = await verifyRazorpayPayment(response, currentPackage, authToken);
          if (result.success) {
            await handlePaymentSuccess(result);
          } else {
            handleVerificationError(new Error(result.error || 'Verification failed'));
          }
        } catch (err) {
          handleVerificationError(err);
        }
      },
      prefill: {
        name: user.name || user.email.split('@')[0],
        email: user.email,
        contact: user.phone || ''
      },
      theme: { color: "#2c5aa0" },
      modal: {
        ondismiss: () => {
          setLoading(false);
          setPaymentStatus('idle');
          setError('Payment was cancelled');
          // Clear stored payment ID on dismissal
          localStorage.removeItem('currentPaymentId');
        }
      },
      notes: {
        display_currency: displayCurrency,
        display_amount: currentPackage.displayPrice,
        user_id: user._id,
        package_id: currentPackage.id
      }
    };

    const rzp = new window.Razorpay(options);
    
    // Add error handling for payment launch
    rzp.on('payment.failed', function (response) {
      console.error('Payment failed:', response.error);
      setError(response.error.description || 'Payment failed');
      setPaymentStatus('error');
      setLoading(false);
      // Clear stored payment ID on failure
      localStorage.removeItem('currentPaymentId');
    });
    
    rzp.open();
  };

  const handleVerificationError = (err) => {
    console.error('Verification error:', err);
    setPaymentStatus('error');
    
    // Clear stored payment ID on error
    localStorage.removeItem('currentPaymentId');
    
    if (err.message === 'SESSION_EXPIRED') {
      setError('Your session expired during payment. Please sign in again.');
      if (logout) logout();
    } else {
      setError(err.message || 'Payment verification failed. Please contact support with your payment ID.');
    }
    setLoading(false);
  };

  // Add a function to check if backend endpoints are available
// ✅ FIXED: Check endpoints properly
const checkPaymentEndpoints = async () => {
  try {
    const endpoints = [
      '/api/payments/health',           // ✅ This endpoint EXISTS and accepts GET
      '/api/payments/create-order',     // Will return 405 (Method Not Allowed) - which is GOOD!
      '/api/payments/verify-payment'    // Will return 405 (Method Not Allowed) - which is GOOD!
    ];

    let availableEndpoints = 0;

    for (const endpoint of endpoints) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          },
          timeout: 3000
        });
        
        // If we get any 2xx response, endpoint exists
        if (response.status >= 200 && response.status < 300) {
          availableEndpoints++;
        }
      } catch (err) {
        // ✅ 405 Method Not Allowed means endpoint EXISTS but wrong method (this is GOOD!)
        if (err.response?.status === 405) {
          availableEndpoints++;
          console.log(`✅ Endpoint exists (405 Method Not Allowed): ${endpoint}`);
        }
        // ✅ 401 Unauthorized means endpoint EXISTS but needs auth (this is GOOD!)
        else if (err.response?.status === 401) {
          availableEndpoints++;
          console.log(`✅ Endpoint exists (401 Unauthorized): ${endpoint}`);
        }
        // ❌ 404 Not Found means endpoint doesn't exist
        else if (err.response?.status === 404) {
          console.log(`❌ Endpoint not found (404): ${endpoint}`);
        }
        // Other errors might mean endpoint exists but has issues
        else if (err.response?.status) {
          availableEndpoints++;
          console.log(`⚠️ Endpoint exists but has issue (${err.response.status}): ${endpoint}`);
        }
      }
    }

    // Consider it available if at least 2 endpoints respond
    return availableEndpoints >= 2;
  } catch (err) {
    console.warn('Endpoint check failed:', err);
    return false;
  }
};


  const handlePaymentError = (err) => {
    console.error('Payment error:', err);
    setPaymentStatus('error');
    
    // Clear stored payment ID on error
    localStorage.removeItem('currentPaymentId');
    
    const errorMessages = {
      'SESSION_EXPIRED': 'Your session has expired. Please sign in again.',
      'NETWORK_ERROR': 'Network connection failed. Please check your internet.',
      'TIMEOUT': 'Request timeout. Please try again.',
      'PAYMENT_SYSTEM_UNAVAILABLE': 'Payment system is currently unavailable. Please try again later or contact support.',
      'INVALID_REQUEST': 'Invalid payment request. Please contact support.',
      'PAYMENT_CANCELLED': 'Payment was cancelled by the user.'
    };

    setError(errorMessages[err.message] || err.message || 'Payment failed. Please try again.');
    setLoading(false);
	
  const queryParams = new URLSearchParams({
    error: err.message,
    payment_id: localStorage.getItem('currentPaymentId')
  }).toString();

  navigate(`/payment/failed?${queryParams}`);


  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setTimeout(resetModalState, 300);
    }
  };

  const handleRetry = () => {
    setError('');
    setPaymentStatus('idle');
    handlePayment();
  };

  const handleSignIn = () => {
    onClose();
    if (logout) logout();
    navigate('/login');
  };

  const steps = [
    { label: 'Confirm', description: 'Review your purchase' },
    { label: 'Processing', description: 'Connecting to payment gateway' },
    { label: 'Verifying', description: 'Confirming transaction' },
    { label: 'Complete', description: 'Payment successful' }
  ];

  if (!currentPackage) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error">No package selected. Please try again.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const pricePerCredit = currentPackage.displayPrice / (currentPackage.credits + (currentPackage.bonus || 0));

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              <Payment sx={{ mr: 1, verticalAlign: 'middle' }} />
              {paymentSuccess ? 'Payment Successful!' : 'Confirm Purchase'}
            </Typography>
            <Button 
              onClick={handleClose} 
              size="small" 
              disabled={loading}
              sx={{ minWidth: 'auto' }}
            >
              <Close />
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Currency Conversion Info */}
          {displayCurrency !== 'INR' && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<CurrencyExchange />}>
              <Typography variant="body2">
                Displayed in {displayCurrency}. Payment processed in INR.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Estimated rate: 1 {displayCurrency} ≈ {exchangeRate.toFixed(2)} INR
              </Typography>
            </Alert>
          )}

          {/* Payment Stepper */}
<Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
  {steps.map((step, index) => (
    <Step key={`${step.label}-${index}`} completed={activeStep > index}>
      <StepLabel 
        sx={{
          '& .MuiStepLabel-label': {
            color: activeStep === index ? 'primary.main' : 'text.secondary',
            fontSize: '0.875rem'
          }
        }}
      >
        {step.label}
        {activeStep === index && loading && (
          <CircularProgress size={14} sx={{ ml: 1 }} />
        )}
      </StepLabel>
    </Step>
  ))}
</Stepper>

          {/* Error Display */}
          {error && (
            <Alert 
              severity={error.includes('expired') ? "warning" : "error"} 
              sx={{ mb: 2 }} 
              onClose={() => setError('')}
              icon={error.includes('expired') ? <Lock /> : undefined}
              action={
                error.includes('expired') ? (
                  <Button color="inherit" size="small" onClick={handleSignIn}>
                    Sign In
                  </Button>
                ) : error.includes('try again') && (
                  <Button color="inherit" size="small" onClick={handleRetry}>
                    Retry
                  </Button>
                )
              }
            >
              {error}
            </Alert>
          )}

          {!razorpayLoaded && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Loading secure payment system...
            </Alert>
          )}

          {paymentSuccess ? (
            // Success View with Invoice Download
            <Box textAlign="center" py={3}>
              <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" gutterBottom color="success.main">
                Payment Successful!
              </Typography>
              <Typography color="text.secondary" paragraph>
                {currentPackage.credits + (currentPackage.bonus || 0)} energy credits have been added to your account.
              </Typography>
              
              {invoiceData && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'success.50', borderRadius: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Invoice #{invoiceData.invoiceNumber} generated
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={downloadInvoice}
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Download Invoice
                  </Button>
                </Box>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This window will close automatically...
              </Typography>
            </Box>
          ) : (
            // Confirmation View
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Package Details
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1">
                    {currentPackage.credits + (currentPackage.bonus || 0)} Energy Credits
                  </Typography>
                  {currentPackage.popular && (
                    <Chip label="MOST POPULAR" color="primary" size="small" />
                  )}
                </Box>

                {currentPackage.bonus > 0 && (
                  <Typography variant="body2" color="success.main" gutterBottom>
                    🎉 +{currentPackage.bonus} bonus credits included!
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Total Amount:</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {displayCurrencySymbol}{currentPackage.displayPrice}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Price per credit:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {displayCurrencySymbol}{pricePerCredit.toFixed(2)}
                  </Typography>
                </Box>

                {/* Currency Conversion Details */}
                {displayCurrency !== 'INR' && (
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      💱 Will be charged as: ₹{currentPackage.inrPrice} INR
                      <br />
                      💳 Conversion handled by your card issuer
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Security color="success" sx={{ fontSize: 16, mr: 1 }} />
                    <Typography variant="caption" fontWeight="bold">
                      Secure Payment
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    🔒 Processed by Razorpay • PCI DSS compliant
                    <br />
                    🔐 Your financial data is never stored on our servers
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          {!paymentSuccess ? (
            <>
              <Button 
                onClick={handleClose} 
                disabled={loading}
                startIcon={<ArrowBack />}
                variant="outlined"
              >
                {paymentStatus === 'idle' ? 'Cancel' : 'Back'}
              </Button>
              <Button
                variant="contained"
                onClick={handlePayment}
                disabled={loading || !razorpayLoaded || paymentStatus !== 'idle'}
                startIcon={loading ? <CircularProgress size={16} /> : <CreditCard />}
                sx={{ minWidth: 140 }}
              >
                {loading ? 
                  (paymentStatus === 'processing' ? 'Creating Order...' : 
                   paymentStatus === 'verifying' ? 'Verifying...' : 'Processing...') 
                  : `Pay ${displayCurrencySymbol}${currentPackage.displayPrice}`}
              </Button>
            </>
          ) : (
            <Button 
              onClick={handleClose}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        message={`Payment successful! ${currentPackage?.credits + (currentPackage?.bonus || 0)} credits added to your account.`}
      />
    </>
  );
};

export default QuantumPurchaseModal;